import { Injectable } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { from, Observable } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';

export interface CwtToastData {
    message?: string;
    params?: any;
    type?: string;
    autoDismiss?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
    constructor(
        private translate: TranslateService,
        private toast: ToastController
    ) { }

    public dismiss(id: string) {
        this.toast.dismiss(id);
    }

    public wrapWithLoading<T>(source: Observable<T>, message: string, params?: any): Observable<T> {
        return this.loading(message, params, false).pipe(
            switchMap(element => {
                return source.pipe(
                    finalize(() => this.dismiss(element))
                )
            })
        );
    }

    private loading(message: string, params?: any, autoDismiss = true) {
        return this._open({
            message: message,
            params: params,
            type: 'primary',
            autoDismiss
        });
    }

    public success(message: string, params?: any, autoDismiss = true) {
        return this._open({
            message: message,
            params: params,
            type: 'success',
            autoDismiss
        }).subscribe();
    }

    public info(message: string, params?: any, autoDismiss = true) {
        return this._open({
            message: message,
            params: params,
            type: 'primary',
            autoDismiss
        }).subscribe();
    }

    public error(message: string, params?: any, autoDismiss = true) {
        return this._open({
            message: message,
            params: params,
            type: 'warning',
            autoDismiss
        }).subscribe();
    }

    private _open(data: CwtToastData) {
        return this.translate.get(data?.message, data?.params).pipe(
            switchMap(i18n => from(this.toast.create({
                animated: true,
                duration: data?.autoDismiss ? 3000 : 0,
                color: data.type,
                message: i18n
            }))),
            tap(toast => toast.present()),
            map(toast => toast.id)
        );
    }
}